/**
 * The table. Every mode in the app renders this exact component.
 *
 * It takes a redacted `View` and a way to send a `GameAction`, and nothing else.
 * The bot game hands it a view computed locally and applies actions straight to
 * an in-memory `GameState`; pass-and-play does the same behind a handoff screen;
 * the room game gets its view over a socket and posts actions back. Because the
 * view is already redacted by the time it arrives, this file has no idea which
 * mode it is in and cannot leak anything even if it wanted to.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { CHIPS, type ChipId } from '@shared/chips';
import type { Answer, GameAction, View } from '@shared/engine';
import { Chip, ChipBack } from '../art/Chip';
import { ChipSheet } from './ChipSheet';
import { useAnnounce } from '../ui/shell';

export interface TableProps {
  view: View;
  act: (action: GameAction) => void;
  /** Connection status, handoff notices — anything the mode wants to say. */
  banner?: React.ReactNode;
  onExit: () => void;
  /** Rendered under the outcome when the game is over. */
  endActions?: React.ReactNode;
}

export function Table({ view, act, banner, onExit, endActions }: TableProps) {
  const announce = useAnnounce();
  const me = view.players.find((p) => p.id === view.you.id);
  const pending = view.you.pending;

  // The most recent loud line is the one worth speaking. Announcing every log
  // entry would talk over the player for the whole game.
  const headline = useMemo(() => {
    for (let i = view.log.length - 1; i >= 0; i--) if (view.log[i]!.loud) return view.log[i]!.text;
    return '';
  }, [view.log]);

  useEffect(() => {
    if (headline) announce(headline);
  }, [headline, announce]);

  useEffect(() => {
    if (pending) announce(pending.prompt);
  }, [pending?.prompt, view.you.pendingId, announce, pending]);

  if (view.phase === 'over' && view.outcome) {
    return <Outcome view={view} onExit={onExit} endActions={endActions} />;
  }

  return (
    <div className="table">
      {banner}
      <TableTop view={view} />
      {/* Scrolls, so it must be focusable — see the note in ui/shell.tsx. */}
      <div className="table-body" tabIndex={0}>
        <Avalon view={view} act={act} />
        <TableList view={view} />
        <PlayedRow view={view} />
        <Narration view={view} />
      </div>
      {/* `data-live` is the single source for "can this player act right now".
          The hand reads it to dim itself, which is a far louder signal than any
          border on the turn bar could be. */}
      <div className="table-foot" data-live={canAct(view) ? 'true' : 'false'}>
        {pending ? (
          <PromptPane key={view.you.pendingId} view={view} act={act} />
        ) : (
          <TurnBar view={view} act={act} />
        )}
        <YourPermanents me={me} />
        <YourHand view={view} act={act} />
        {me ? null : <p className="muted">You are watching this game.</p>}
      </div>
    </div>
  );
}

/**
 * Can this player do anything at this instant?
 *
 * The one predicate the whole "is it my turn" question reduces to. A prompt
 * addressed to you, a draw you still owe, or a fist you have not closed.
 */
function canAct(view: View): boolean {
  if (view.you.pending) return true;
  if (view.phase === 'draw') return view.you.drawsOwed > 0;
  if (view.phase === 'play') {
    return !view.you.played && view.you.hand.some((c) => CHIPS[c].gender !== 'sword');
  }
  return false;
}

// ── chrome ──────────────────────────────────────────────────────────────────

function TableTop({ view }: { view: View }) {
  const crown = view.players.find((p) => p.isCrown);
  // "Crown: Player" is what this read when the crown was yours, because the
  // local player's default name is literally "Player". Second person is both
  // correct and shorter.
  const crownLabel = crown ? (crown.id === view.you.id ? 'you' : crown.name) : null;
  return (
    <div className="table-top">
      <span className="eyebrow">Round {view.round}</span>
      {crownLabel ? <span className="eyebrow">Crown: {crownLabel}</span> : null}
      {view.finalRound ? <span className="eyebrow hot">Final round</span> : null}
    </div>
  );
}

/**
 * Avalon's three regions.
 *
 * Only the counts are known — the view never carries what is in them. The counts
 * are the whole strategic surface though, because an empty region ends the game,
 * so they are rendered large and never hidden behind a tap.
 */
function Avalon({ view, act }: { view: View; act: (a: GameAction) => void }) {
  const pending = view.you.pending;
  const drawing = view.phase === 'draw' && view.you.drawsOwed > 0 && !pending;
  const choosingRegion = pending?.kind === 'region';
  const live = choosingRegion || drawing;

  const onPick = (i: number) => {
    if (choosingRegion) act({ t: 'answer', pendingId: view.you.pendingId, answer: { kind: 'region', region: i } });
    else if (drawing) act({ t: 'draw', region: i });
  };

  const allowEmpty = choosingRegion && pending.allowEmpty;

  return (
    <section className="avalon" aria-label="Avalon">
      <h2 className="section-title">Avalon</h2>
      <div className="region-row">
        {BLADES.map((blade, i) => {
          const n = view.regions[i]!;
          const usable = live && (allowEmpty || n > 0);
          return (
            <button
              key={blade}
              type="button"
              className={`region${usable ? ' region--live' : ''}${n === 0 ? ' region--empty' : ''}`}
              disabled={!usable}
              onClick={() => onPick(i)}
              aria-label={`${blade} region, ${n} chip${n === 1 ? '' : 's'}${usable ? '. Choose this region.' : ''}`}
            >
              <span className="region__stack" aria-hidden="true">
                {n === 0 ? <span className="region__gone">empty</span> : <ChipBack size="sm" />}
              </span>
              <span className="region__count">{n}</span>
              <span className="region__name">{blade}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** The blade replicas are dividers, not chips — they have no powers at all. */
const BLADES = ['Arondight', 'Galatine', 'Excalibur'] as const;

/**
 * The table, in the order the round will actually happen.
 *
 * During Draw and Play there is no order to show — everyone acts at once — so
 * this is a plain roster. From Reveal onwards it re-sorts into `view.order` and
 * becomes the turn list, which is the only place "who goes after this" is
 * answerable.
 *
 * Your own row is included from Reveal onwards. It used to be filtered out
 * entirely, which meant you could never see your own position in the order, and
 * the crown mark never rendered on you even when the crown was yours.
 */
function TableList({ view }: { view: View }) {
  const ordered = view.phase === 'reveal' || view.phase === 'resolve';
  const activeId = view.active?.playerId ?? view.waitingOn?.playerId ?? null;
  const activeRow = useRef<HTMLLIElement | null>(null);

  // At eight players the list is taller than the scroll area, so the row that
  // matters is regularly off screen. Nudge it back into view when the baton moves.
  useEffect(() => {
    activeRow.current?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const rows = ordered
    ? view.order.map((id) => view.players.find((p) => p.id === id)).filter((p): p is View['players'][number] => !!p)
    : view.players.filter((p) => p.id !== view.you.id);

  if (rows.length === 0) return null;

  // The tag only earns its width when it tells two OPPONENTS apart. Against a
  // table of nothing but bots it is four identical labels saying what you already
  // know, and it pushes the chip count onto a second line.
  const opponents = view.players.filter((p) => p.id !== view.you.id);
  const mixed = opponents.some((p) => p.isBot) && opponents.some((p) => !p.isBot);
  const doneCount = view.active ? view.active.index - 1 : 0;

  return (
    <section className="opponents" aria-label={ordered ? 'Turn order this round' : 'The table'}>
      <h2 className="section-title">{ordered ? 'Turn order — this round' : 'The table'}</h2>
      <ol className="opponent-list">
        {rows.map((p, i) => {
          const isMe = p.id === view.you.id;
          const isActive = ordered && p.id === activeId;
          const isDone = ordered && !isActive && i < doneCount;
          const cls = [
            'opponent',
            isActive ? 'opponent--active' : '',
            isDone ? 'opponent--done' : '',
            isMe ? 'opponent--you' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <li key={p.id} className={cls} ref={isActive ? activeRow : undefined}>
              {ordered ? (
                <span className="opponent__ord" aria-hidden="true">
                  {isDone ? '\u2713' : isActive ? '\u25B8' : i + 1}
                </span>
              ) : null}
              <span className="opponent__name">
                {p.isCrown ? <span aria-hidden="true">{'\u2654 '}</span> : null}
                {isMe ? 'You' : p.name}
                {mixed && p.isBot ? <span className="tag">computer</span> : null}
              </span>
              <span className="opponent__meta">
                <span
                  className="opponent__chips"
                  aria-label={`${p.handCount} chip${p.handCount === 1 ? '' : 's'} in hand`}
                >
                  {p.handCount} <span aria-hidden="true">{p.handCount === 1 ? 'chip' : 'chips'}</span>
                </span>
                {p.hasPlayed && view.phase === 'play' ? <span className="tag tag--ready">fist closed</span> : null}
                {p.fisherKingUp ? <span className="tag tag--ward">warded</span> : null}
                {p.protectedCount > 0 ? <span className="tag">shielded</span> : null}
              </span>
              {/* Said in words as well as with a bar, because colour alone is not
                  a signal and a glyph alone is not a name. */}
              {isActive ? <span className="visually-hidden">Acting now.</span> : null}
              {isDone ? <span className="visually-hidden">Already resolved.</span> : null}
              {p.permanents.length > 0 ? (
                <span className="opponent__perms">
                  {p.permanents.map((c, k) => (
                    <Chip key={`${c}${k}`} id={c} size="sm" label={`${isMe ? 'You have' : `${p.name} has`} ${CHIPS[c].name} in play`} />
                  ))}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      {ordered ? (
        <p className="opponent__wrap" aria-hidden="true">
          {'\u21BB back to the crown'}
        </p>
      ) : null}
    </section>
  );
}

/** Fists open. Everything here is public, which is most of the game's memory. */
function PlayedRow({ view }: { view: View }) {
  if (view.phase !== 'reveal' && view.phase !== 'resolve') return null;
  const played = view.players.filter((p) => p.played);
  if (played.length === 0) return null;
  return (
    <section className="played" aria-label="Chips in play this round">
      <h2 className="section-title">Played this round</h2>
      <div className="played-row">
        {played.map((p) => (
          <figure key={p.id} className={`played-card${p.playedKilled ? ' played-card--dead' : ''}`}>
            <Chip id={p.played!} size="md" label={`${p.name} played ${CHIPS[p.played!].name}`} />
            <figcaption>
              {p.name}
              {p.playedKilled ? ' — struck down' : ''}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function Narration({ view }: { view: View }) {
  const lines = view.log.slice(-6);
  if (lines.length === 0) return null;
  return (
    <section className="narration" aria-label="What just happened">
      <ol className="log">
        {lines.map((l, i) => (
          <li key={`${l.round}-${i}-${l.text}`} className={l.loud ? 'log__line log__line--loud' : 'log__line'}>
            {l.text}
          </li>
        ))}
      </ol>
    </section>
  );
}

// ── your turn ───────────────────────────────────────────────────────────────

/**
 * The turn bar. Always in the same place, radically different depending on
 * whether the table is waiting on you.
 *
 * Two of Excalibur's five steps have no turns at all — Draw and Play are
 * simultaneous — and one, Resolve, is a strict baton passed clockwise from the
 * crown. Rendering both through one "Waiting for X" sentence was the bug: a
 * checklist and a baton need different grammar, not different interpolations.
 *
 * The strongest signal here is not in this component at all. When you cannot
 * act, `.table-foot[data-live="false"]` dims the hand — a half-screen change
 * that no border can compete with, and an honest one, because the chips really
 * are inert then.
 */
function TurnBar({ view, act }: { view: View; act: (a: GameAction) => void }) {
  const s = turnState(view);

  return (
    <div className={`baton${s.live ? ' baton--live' : ''}`}>
      {/*
        Only the eyebrow is a live region. Making the whole bar one would re-read
        the question every time a chip count ticked; this way a screen reader is
        told "Bedivere's turn" once, when it becomes true.
      */}
      <p className="baton__eyebrow" role="status" aria-live="polite" aria-atomic="true">
        <span className="baton__glyph" aria-hidden="true">
          {s.live ? '▶' : '◦'}
        </span>
        {s.eyebrow}
      </p>
      <p className="baton__text">{s.text}</p>
      {s.then ? <p className="baton__then">{s.then}</p> : null}
      {view.phase === 'play' && view.you.played ? (
        <div className="prompt__row">
          <button type="button" className="btn ghost" onClick={() => act({ t: 'unplay' })}>
            Take it back
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface TurnState {
  /** Whether the table is waiting on THIS player. Drives every visual channel. */
  live: boolean;
  eyebrow: string;
  text: string;
  then: string | null;
}

/**
 * One place that decides what the bar says, so the strings can be read in order
 * and argued about without reading JSX.
 */
function turnState(view: View): TurnState {
  const meId = view.you.id;

  // ── the two simultaneous steps: a checklist, not a turn ──────────────────
  if (view.phase === 'draw') {
    const owed = view.you.drawsOwed;
    if (owed > 0) {
      return {
        live: true,
        eyebrow: 'Your move · everyone at once',
        text: `Draw back up to four. Tap a region — ${owed} to go.`,
        then: null,
      };
    }
    const waiting = view.players.filter((p) => p.id !== meId && p.handCount < 4);
    return {
      live: false,
      eyebrow: 'Draw · everyone at once',
      text: `You're up to four. ${waitingOnPhrase(waiting.map((p) => p.name))}`,
      then: null,
    };
  }

  if (view.phase === 'play') {
    if (!view.you.played) {
      const playable = view.you.hand.filter((c) => CHIPS[c].gender !== 'sword');
      if (playable.length === 0) {
        return {
          live: false,
          eyebrow: 'Play · everyone at once',
          text: 'You hold nothing but swords. You sit this round out.',
          then: null,
        };
      }
      return {
        live: true,
        eyebrow: 'Your move · everyone at once',
        text: 'Close your fist on one chip.',
        then: null,
      };
    }
    const waiting = view.players.filter((p) => p.id !== meId && !p.hasPlayed);
    return {
      live: false,
      eyebrow: 'Play · everyone at once',
      text: `Your fist is closed on ${CHIPS[view.you.played].name}. ${waitingOnPhrase(waiting.map((p) => p.name))}`,
      then: null,
    };
  }

  // ── the ordered steps: a baton ────────────────────────────────────────────
  const active = view.active;
  const stepName = active?.step === 'reveal' ? 'Reveal' : 'Resolve';
  const position = active ? ` · ${active.index} of ${active.total}` : '';

  if (view.you.pending) {
    // PromptPane renders the question itself; the bar carries the heading.
    return {
      live: true,
      eyebrow: `Your turn${position}`,
      text: '',
      then: thenPhrase(view),
    };
  }

  const actorId = view.waitingOn?.playerId ?? active?.playerId ?? null;
  if (!actorId) {
    // Genuinely nothing to name — between steps, or a spectator's view. Never
    // "waiting for the round", which names nobody and helps no one.
    return { live: false, eyebrow: stepName, text: 'The round is resolving.', then: null };
  }

  const who = nameFor(view, actorId);
  const chip = view.players.find((p) => p.id === actorId)?.played;
  const doing = view.waitingOn
    ? `${who} is choosing${chip ? ` — ${CHIPS[chip].name}` : ''}.`
    : `${who}'s ${chip ? CHIPS[chip].name : 'chip'} is resolving.`;

  return {
    live: false,
    eyebrow: `${stepName}${position} · ${possessive(who)} turn`,
    text: doing,
    then: thenPhrase(view),
  };
}

/** "Waiting on Bedivere." / "…and Elaine." / "…and 2 others." */
function waitingOnPhrase(names: string[]): string {
  if (names.length === 0) return 'Waiting on the table.';
  if (names.length === 1) return `Waiting on ${names[0]}.`;
  if (names.length === 2) return `Waiting on ${names[0]} and ${names[1]}.`;
  return `Waiting on ${names[0]} and ${names.length - 1} others.`;
}

/**
 * Who goes after the player currently holding the baton.
 *
 * This is the half of "whose turn is it" that a name alone cannot answer, and
 * the only thing on screen that makes the crown's job visible.
 */
function thenPhrase(view: View): string | null {
  const active = view.active;
  if (!active) return null;
  const order = view.order;
  const at = order.indexOf(active.playerId);
  if (at < 0) return null;
  const rest = order.slice(at + 1);
  if (rest.length === 0) return 'Last to resolve.';
  const label = (id: string) => (id === view.you.id ? 'you' : nameFor(view, id));
  if (rest.length === 1) return `Then ${label(rest[0]!)}.`;
  if (rest.length === 2) return `Then: ${label(rest[0]!)}, then ${label(rest[1]!)}.`;
  return `Then: ${label(rest[0]!)}, ${label(rest[1]!)}, +${rest.length - 2}.`;
}

/** "Bedivere's" / "Bedivere's" — and "your" for the local player. */
function possessive(name: string): string {
  return name.endsWith('s') ? `${name}'` : `${name}'s`;
}

function nameFor(view: View, id: string): string {
  return view.players.find((p) => p.id === id)?.name ?? 'someone';
}

/**
 * Your own faceup chips in play.
 *
 * Every other player's permanents show in the opponent list, and yours showed
 * nowhere at all — a Fisher King you had played and were about to be asked to
 * flip was simply invisible to you, because it leaves your fist at reveal and is
 * never part of your hand.
 */
function YourPermanents({ me }: { me: View['players'][number] | undefined }) {
  if (!me || me.permanents.length === 0) return null;
  return (
    <section className="in-play" aria-label="In play before you">
      <h2 className="section-title">Before you</h2>
      <div className="hand__row">
        {me.permanents.map((c, i) => (
          <Chip
            key={`${c}-${i}`}
            id={c}
            size="sm"
            label={`${CHIPS[c].name} is in play before you. ${CHIPS[c].text}`}
          />
        ))}
        {me.fisherKingUp ? <span className="tag tag--ward">warded</span> : null}
      </div>
    </section>
  );
}

function YourHand({ view, act }: { view: View; act: (a: GameAction) => void }) {
  const pending = view.you.pending;
  const canPlay = view.phase === 'play' && !view.you.played && !pending;
  // While a prompt is asking which chips to hand over, the hand is the answer
  // surface and `PromptPane` owns it. Two tappable copies of the same hand on
  // one screen is how you return the wrong chip.
  const promptOwnsHand = pending?.kind === 'hand';

  /**
   * Which chip is being looked at before committing, by INDEX.
   *
   * Index rather than id because a hand routinely holds two Merchants, and
   * "which Merchant am I looking at" has to survive the hand being reordered by
   * a steal between renders.
   */
  const [preview, setPreview] = useState<number | null>(null);

  // A view can arrive that ends the play step — somebody else's last fist closed,
  // or a reconnect replaced the whole state — while this sheet is open. Leaving
  // it up would offer to play a chip that is no longer in hand.
  useEffect(() => {
    if (!canPlay) setPreview(null);
  }, [canPlay]);

  const chosen = preview !== null ? view.you.hand[preview] : undefined;

  return (
    <section className="hand" aria-label="Your hand">
      <div className="hand__head">
        <h2 className="section-title">
          Your hand <span className="muted">({view.you.hand.length})</span>
        </h2>
      </div>
      {/* The hand scrolls sideways once you hold more than five or six chips,
          which Robyn of Roxley guarantees at least once a game. */}
      <div className="hand__row" tabIndex={0}>
        {view.you.hand.length === 0 ? <p className="muted">Empty.</p> : null}
        {view.you.hand.map((c, i) => {
          const isSword = CHIPS[c].gender === 'sword';
          const playable = canPlay && !isSword;
          return (
            <Chip
              key={`${c}-${i}`}
              id={c}
              size="sm"
              targetable={playable}
              selected={preview === i}
              disabled={promptOwnsHand}
              protectedChip={view.you.protectedChip === c}
              // The first tap only opens the sheet. Committing takes a second,
              // deliberate tap — the chip is 56px wide and its ability text is
              // not on it, so one-tap-to-commit was asking people to guess.
              onClick={playable ? () => setPreview(i) : undefined}
              label={
                isSword
                  ? `${CHIPS[c].name}. A sword — it can never be played.`
                  : `${CHIPS[c].name}. ${CHIPS[c].text}${playable ? ' Look at this chip before playing it.' : ''}`
              }
            />
          );
        })}
      </div>

      {chosen ? (
        <ChipSheet
          chip={chosen}
          eyebrow="Play this chip?"
          confirmLabel="Lock it in"
          cancelLabel="Choose another"
          note="Your fist stays closed until everyone has chosen. You can still take it back."
          onConfirm={() => {
            setPreview(null);
            act({ t: 'play', chip: chosen });
          }}
          onCancel={() => setPreview(null)}
        />
      ) : null}
    </section>
  );
}

// ── prompts ─────────────────────────────────────────────────────────────────

/**
 * One pane per kind of question the engine can ask.
 *
 * Keyed on `pendingId` by the caller, so every local selection resets the moment
 * the question changes. Without that key, a half-made selection from the last
 * prompt survives into the next one and gets submitted as its answer.
 */
function PromptPane({ view, act }: { view: View; act: (a: GameAction) => void }) {
  const p = view.you.pending!;
  const answer = (a: Answer) => act({ t: 'answer', pendingId: view.you.pendingId, answer: a });

  return (
    // `data-kind` is a stable hook for the test driver, which has to answer
    // eleven structurally different questions and cannot tell a "pick one chip"
    // tray from a "choose two to place" tray by shape alone. A data attribute is
    // fine under the CSP — it is only `style` that is forbidden.
    <div className="prompt prompt--live" data-kind={p.kind}>
      <p className="prompt__source">{CHIPS[p.chip].name}</p>
      <p className="prompt__text">{p.prompt}</p>
      {p.kind === 'mode' ? (
        <div className="prompt__row">
          {p.modes.map((m) => (
            <button key={m.id} type="button" className="btn" onClick={() => answer({ kind: 'mode', mode: m.id })}>
              {m.label}
            </button>
          ))}
        </div>
      ) : null}

      {p.kind === 'direction' ? (
        <div className="prompt__row">
          <button type="button" className="btn" onClick={() => answer({ kind: 'direction', direction: 'left' })}>
            To the left
          </button>
          <button type="button" className="btn" onClick={() => answer({ kind: 'direction', direction: 'right' })}>
            To the right
          </button>
        </div>
      ) : null}

      {p.kind === 'confirm' ? (
        <div className="prompt__row">
          <button type="button" className="btn" onClick={() => answer({ kind: 'confirm', yes: true })}>
            {p.yes}
          </button>
          <button type="button" className="btn ghost" onClick={() => answer({ kind: 'confirm', yes: false })}>
            {p.no}
          </button>
        </div>
      ) : null}

      {p.kind === 'players' ? <PlayerPicker view={view} pending={p} answer={answer} /> : null}
      {p.kind === 'hand' ? <HandPicker view={view} pending={p} answer={answer} /> : null}

      {p.kind === 'pick' ? (
        <>
          <div className="chip-tray">
            {p.chips.map((c, i) => (
              <Chip key={`${c}-${i}`} id={c} size="md" targetable onClick={() => answer({ kind: 'pick', index: i })} />
            ))}
          </div>
          {p.optional ? (
            <div className="prompt__row">
              <button type="button" className="btn ghost" onClick={() => answer({ kind: 'pick', index: null })}>
                Cast nothing
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {p.kind === 'peek' ? (
        <>
          <div className="chip-tray">
            {p.peek.length === 0 ? <p className="muted">Their hand is empty.</p> : null}
            {p.peek.map((c, i) => (
              <Chip
                key={`${c}-${i}`}
                id={c}
                size="md"
                targetable={p.mode === 'expose'}
                onClick={p.mode === 'expose' ? () => answer({ kind: 'peek', index: i }) : undefined}
              />
            ))}
          </div>
          {p.mode === 'swap' ? (
            <div className="prompt__row">
              <button type="button" className="btn" onClick={() => answer({ kind: 'peek', index: null, swap: true })}>
                Swap hands
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => answer({ kind: 'peek', index: null, swap: false })}
              >
                Leave it
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {p.kind === 'sword' ? (
        <div className="chip-tray">
          {p.swords.map((c) => (
            <Chip key={c} id={c} size="md" targetable onClick={() => answer({ kind: 'sword', chip: c })} />
          ))}
        </div>
      ) : null}

      {p.kind === 'played' ? (
        <div className="chip-tray">
          {p.options.map((o, i) => (
            <figure key={`${o.ownerId}-${o.chip}-${i}`} className="played-card">
              <Chip
                id={o.chip}
                size="md"
                targetable
                onClick={() => answer({ kind: 'played', ownerId: o.ownerId, chip: o.chip })}
                label={`Take ${CHIPS[o.chip].name} from ${nameFor(view, o.ownerId)}`}
              />
              <figcaption>{nameFor(view, o.ownerId)}</figcaption>
            </figure>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PlayerPicker({
  view,
  pending,
  answer,
}: {
  view: View;
  pending: Extract<NonNullable<View['you']['pending']>, { kind: 'players' }>;
  answer: (a: Answer) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const toggle = (id: string) => {
    setPicked((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (pending.max === 1) return [id];
      if (cur.length >= pending.max) return cur;
      return [...cur, id];
    });
  };

  // A single-target prompt should resolve on the tap, not on a second confirm tap.
  const immediate = pending.max === 1 && pending.min === 1;
  const ready = picked.length >= pending.min && picked.length <= pending.max;

  return (
    <>
      <div className="picker">
        {pending.candidates.map((id) => (
          <button
            key={id}
            type="button"
            className={`picker__item${picked.includes(id) ? ' picker__item--on' : ''}`}
            onClick={() => (immediate ? answer({ kind: 'players', players: [id] }) : toggle(id))}
          >
            {nameFor(view, id)}
            <span className="picker__sub">
              {view.players.find((p) => p.id === id)?.handCount ?? 0} chips
            </span>
          </button>
        ))}
      </div>
      {immediate ? null : (
        <div className="prompt__row">
          <button
            type="button"
            className="btn"
            disabled={!ready}
            onClick={() => answer({ kind: 'players', players: picked })}
          >
            {picked.length === 0 ? 'Point at nobody' : `Confirm (${picked.length})`}
          </button>
        </div>
      )}
    </>
  );
}

function HandPicker({
  view,
  pending,
  answer,
}: {
  view: View;
  pending: Extract<NonNullable<View['you']['pending']>, { kind: 'hand' }>;
  answer: (a: Answer) => void;
}) {
  // Indices, not chip ids: a hand can hold two Merchants and picking "a
  // Merchant" twice must mean two different physical chips.
  const [picked, setPicked] = useState<number[]>([]);

  // The prompt lists what is eligible as a multiset; map that onto the actual
  // hand so a Baroness cannot offer to trash the sword sitting next to it.
  const eligibleIdx = useMemo(() => {
    const pool = pending.eligible.slice();
    const out: number[] = [];
    view.you.hand.forEach((c, i) => {
      const at = pool.indexOf(c);
      if (at >= 0) {
        pool.splice(at, 1);
        out.push(i);
      }
    });
    return out;
  }, [pending.eligible, view.you.hand]);

  const toggle = (i: number) => {
    setPicked((cur) => {
      if (cur.includes(i)) return cur.filter((x) => x !== i);
      if (pending.max === 1) return [i];
      if (cur.length >= pending.max) return cur;
      return [...cur, i];
    });
  };

  const ready = picked.length >= pending.min && picked.length <= pending.max;
  const submit = () => answer({ kind: 'hand', chips: picked.map((i) => view.you.hand[i]!) as ChipId[] });
  const immediate = pending.max === 1 && pending.min === 1;

  return (
    <>
      <div className="chip-tray chip-tray--hand">
        {view.you.hand.map((c, i) => {
          const ok = eligibleIdx.includes(i);
          return (
            <Chip
              key={`${c}-${i}`}
              id={c}
              size="sm"
              selected={picked.includes(i)}
              disabled={!ok}
              targetable={ok && !picked.includes(i)}
              onClick={
                ok
                  ? () =>
                      immediate
                        ? answer({ kind: 'hand', chips: [c] })
                        : toggle(i)
                  : undefined
              }
            />
          );
        })}
      </div>
      {immediate ? null : (
        <div className="prompt__row">
          <button type="button" className="btn" disabled={!ready} onClick={submit}>
            Confirm ({picked.length}/{pending.max})
          </button>
        </div>
      )}
    </>
  );
}

// ── the end ─────────────────────────────────────────────────────────────────

function Outcome({ view, onExit, endActions }: { view: View; onExit: () => void; endActions?: React.ReactNode }) {
  const out = view.outcome!;
  const won = out.winners.includes(view.you.id);
  const cursed = out.ultimateLoser === view.you.id;

  const verdict = cursed
    ? 'The Cursed Blade is yours.'
    : won
      ? out.tainted
        ? 'A tainted victory.'
        : 'The crown is yours.'
      : out.winners.length === 0
        ? 'The kingdom falls.'
        : 'You are not the ruler.';

  return (
    <div className="table table--over">
      <div className="table-body">
        <p className="eyebrow hot">Round {view.round} — the end</p>
        <h1 className="verdict">{verdict}</h1>
        <p className="headline">{out.headline}</p>

        <section aria-label="Final hands" className="final">
          <h2 className="section-title">Where the swords finished</h2>
          <ul className="final-list">
            {view.players.map((p) => (
              <li key={p.id} className={out.winners.includes(p.id) ? 'final-row final-row--won' : 'final-row'}>
                <span className="final-row__name">
                  {p.name}
                  {p.id === view.you.id ? ' (you)' : ''}
                </span>
                <span className="final-row__chips">
                  {(p.hand ?? []).filter((c) => CHIPS[c].gender === 'sword').length === 0 ? (
                    <span className="muted">no sword</span>
                  ) : (
                    (p.hand ?? [])
                      .filter((c) => CHIPS[c].gender === 'sword')
                      .map((c) => <Chip key={c} id={c} size="sm" />)
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <div className="actions">
        {endActions}
        <button type="button" className="btn ghost" onClick={onExit}>
          Leave the table
        </button>
      </div>
    </div>
  );
}
